class DbrSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="DbrSlider-"]');
    this.sliderItems = this.querySelectorAll('[id^="DbrSlide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.dbr-slideshow__counter-current');
    this.pageTotalElement = this.querySelector('.dbr-slideshow__counter-total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    new ResizeObserver(() => this.initPages()).observe(this.slider);
    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  update() {
    if (!this.slider || !this.nextButton) return;
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
    if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(el, offset = 0) {
    const lastVisible = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return el.offsetLeft + el.clientWidth <= lastVisible && el.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset?.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({ left: position, behavior: 'smooth' });
  }
}

class DbrSlideshowComponent extends DbrSliderComponent {
  constructor() {
    super();
    this.enableSliderLooping = true;
    this.isFadeMode = this.dataset.transition === 'fade';
    this.dotsArray = Array.from(this.querySelectorAll('.dbr-slideshow__dot'));
    this.progressFill = this.querySelector('.dbr-slideshow__progress-fill');
    this.autoplayButton = this.querySelector('.dbr-slideshow__autoplay');

    if (this.isFadeMode) {
      this.initFadeMode();
    } else {
      if (!this.slider || !this.sliderItemsToShow?.length) return;
      this.firstSlideNode = this.slider.querySelector('.dbr-slideshow__slide');
      this.currentPage = 1;
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      this.setSlideVisibility();
    }

    this.dotsArray.forEach((dot) => dot.addEventListener('click', this.onDotClick.bind(this)));

    this.initTouch();
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    if (this.dataset.autoplay === 'true') this.initAutoPlay();
  }

  // ── Fade mode ─────────────────────────────────────────────

  initFadeMode() {
    this.currentPage = 1;
    this.sliderItemsToShow = Array.from(this.sliderItems);
    this.sliderItemOffset = 0;
    this.totalPages = this.sliderItemsToShow.length;

    this.sliderItemsToShow.forEach((slide, i) => {
      if (i === 0) {
        slide.classList.add('is-active');
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      }
    });

    this.updateDots(1);
    if (this.currentPageElement) this.currentPageElement.textContent = 1;
    if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;
  }

  fadeToSlide(pageNumber) {
    const currentSlide = this.sliderItemsToShow[this.currentPage - 1];
    const nextSlide = this.sliderItemsToShow[pageNumber - 1];
    if (!nextSlide || currentSlide === nextSlide) return;

    // Lock container height to prevent layout shift during transition
    const h = this.slider.offsetHeight;
    this.slider.style.minHeight = `${h}px`;

    currentSlide.classList.remove('is-active');
    currentSlide.setAttribute('aria-hidden', 'true');
    currentSlide.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));

    nextSlide.classList.add('is-active');
    nextSlide.removeAttribute('aria-hidden');
    nextSlide.querySelectorAll('a, button').forEach((el) => el.removeAttribute('tabindex'));

    this.currentPage = pageNumber;
    this.updateDots(pageNumber);
    if (this.currentPageElement) this.currentPageElement.textContent = pageNumber;

    setTimeout(() => {
      this.slider.style.minHeight = '';
    }, 700);
  }

  // ── Navigation ────────────────────────────────────────────

  updateDots(page) {
    this.dotsArray.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === page - 1);
      dot.setAttribute('aria-current', i === page - 1 ? 'true' : 'false');
    });
  }

  onDotClick(event) {
    event.preventDefault();
    const index = this.dotsArray.indexOf(event.currentTarget) + 1;
    if (this.isFadeMode) {
      this.fadeToSlide(index);
    } else {
      const pos = this.slider.scrollLeft + this.firstSlideNode.clientWidth * (index - this.currentPage);
      this.slider.scrollTo({ left: pos });
    }
    this.restartAutoplay();
  }

  onButtonClick(event) {
    event.preventDefault();

    if (this.isFadeMode) {
      const isNext = event.currentTarget.name === 'next';
      const next = isNext
        ? this.currentPage === this.totalPages ? 1 : this.currentPage + 1
        : this.currentPage === 1 ? this.totalPages : this.currentPage - 1;
      this.fadeToSlide(next);
      this.restartAutoplay();
      return;
    }

    const isFirst = this.currentPage === 1;
    const isLast = this.currentPage === this.sliderItemsToShow.length;

    if (isFirst && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.firstSlideNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLast && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    } else {
      super.onButtonClick(event);
      this.restartAutoplay();
      return;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.restartAutoplay();
  }

  update() {
    if (this.isFadeMode) return;
    super.update();
    this.prevButton?.removeAttribute('disabled');
    this.updateDots(this.currentPage);
  }

  setSlideVisibility() {
    if (this.isFadeMode) return;
    if (!this.sliderItemsToShow?.length) return;
    this.sliderItemsToShow.forEach((item, i) => {
      const active = i === this.currentPage - 1;
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
      item.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
  }

  // ── Touch / swipe ─────────────────────────────────────────

  initTouch() {
    let startX = 0;
    const target = this.slider || this;

    target.addEventListener('touchstart', (e) => { startX = e.changedTouches[0].screenX; }, { passive: true });
    target.addEventListener(
      'touchend',
      (e) => {
        const delta = startX - e.changedTouches[0].screenX;
        if (Math.abs(delta) < 50) return;
        this.onButtonClick({
          currentTarget: { name: delta > 0 ? 'next' : 'previous', dataset: {} },
          preventDefault: () => {},
        });
      },
      { passive: true }
    );
  }

  // ── Keyboard ──────────────────────────────────────────────

  onKeyDown(event) {
    if (event.key === 'ArrowRight')
      this.onButtonClick({ currentTarget: { name: 'next', dataset: {} }, preventDefault: () => {} });
    if (event.key === 'ArrowLeft')
      this.onButtonClick({ currentTarget: { name: 'previous', dataset: {} }, preventDefault: () => {} });
  }

  // ── Autoplay ──────────────────────────────────────────────

  initAutoPlay() {
    this.autoplaySpeed = parseInt(this.dataset.speed || '5') * 1000;
    this.autoplayEnabled = true;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('mouseover', () => this.pause());
    this.addEventListener('mouseleave', () => { if (this.autoplayEnabled) this.play(); });
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget) && this.autoplayEnabled) this.play();
    });

    if (this.autoplayButton) {
      this.autoplayButton.addEventListener('click', this.toggleAutoplay.bind(this));
    }

    this.reducedMotion.addEventListener('change', () => {
      this.reducedMotion.matches ? this.pause() : (this.autoplayEnabled && this.play());
    });

    if (!this.reducedMotion.matches) this.play();
  }

  play() {
    if (!this.autoplaySpeed) return;
    this.slider?.setAttribute('aria-live', 'off');
    clearInterval(this._timer);
    this._timer = setInterval(() => this.autoRotate(), this.autoplaySpeed);
    this.startProgressBar();
  }

  pause() {
    this.slider?.setAttribute('aria-live', 'polite');
    clearInterval(this._timer);
    this.pauseProgressBar();
  }

  restartAutoplay() {
    if (this.autoplayEnabled && this.autoplaySpeed) {
      clearInterval(this._timer);
      this.play();
    }
  }

  toggleAutoplay() {
    this.autoplayEnabled = !this.autoplayEnabled;
    this.autoplayEnabled ? this.play() : this.pause();
    this.autoplayButton?.classList.toggle('is-paused', !this.autoplayEnabled);
    this.autoplayButton?.setAttribute(
      'aria-label',
      this.autoplayEnabled ? 'Pausar slideshow' : 'Iniciar slideshow'
    );
  }

  autoRotate() {
    if (this.isFadeMode) {
      const next = this.currentPage === this.totalPages ? 1 : this.currentPage + 1;
      this.fadeToSlide(next);
      this.startProgressBar();
    } else {
      const pos =
        this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;
      this.setSlidePosition(pos);
    }
  }

  // ── Progress bar ──────────────────────────────────────────

  startProgressBar() {
    if (!this.progressFill) return;
    this.progressFill.style.transition = 'none';
    this.progressFill.style.width = '0%';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.progressFill.style.transition = `width ${this.autoplaySpeed}ms linear`;
        this.progressFill.style.width = '100%';
      });
    });
  }

  pauseProgressBar() {
    if (!this.progressFill) return;
    const current = parseFloat(getComputedStyle(this.progressFill).width);
    const total = this.progressFill.parentElement.clientWidth;
    this.progressFill.style.transition = 'none';
    this.progressFill.style.width = `${(current / total) * 100}%`;
  }
}

customElements.define('dbr-slideshow-component', DbrSlideshowComponent);
